import { FullEntityResponse } from 'app/modules/entities/responses';

import { ENTITY_QUERY_KEYS } from 'app/modules/entities/queries/keys';

import { get, getWithParsedError } from 'app/shared/utils/fetchr';
import { useQuery } from '@tanstack/react-query';
import { selectDLEnabledForEntityDetails } from 'app/shared/featureFlags/selectors';
import { useSelector } from 'react-redux';
import { selectHasReadEntitiesPermissions } from 'app/modules/session/selectors';
import { useRef, useMemo, useEffect } from 'react';
import { CGDOLoadingResponse } from 'app/modules/investigations/types/responses';
import {
  getLoadingFromColdStorageStateForCGDO,
  getColdStorageRetryWaitTime,
  COLD_STORAGE_MAX_RETRIES,
  isCGDOLoadingResponse,
} from 'app/shared/utils/fastArchHelpers';
import { useCGDOProgressiveData } from 'app/shared/hooks/useCGDOProgressiveData';

type EntityDetailsResponse =
  | FullEntityResponse
  | (FullEntityResponse & CGDOLoadingResponse)
  | CGDOLoadingResponse;

export const useEntityDetails = <T = FullEntityResponse>(
  entityId: string,
  select: (data: FullEntityResponse) => T = (data) => data as T,
  enabled: boolean = true,
  isExternalEntityId: boolean = false,
) => {
  const useCacheEndpoint = useSelector(selectDLEnabledForEntityDetails);
  const hasReadEntitiesPermission = useSelector(
    selectHasReadEntitiesPermissions,
  );
  const dataUpdateCountRef = useRef(0);

  const query = useQuery<EntityDetailsResponse>({
    queryFn: async () => {
      if (useCacheEndpoint) {
        return getWithParsedError<EntityDetailsResponse>(
          `/v1/entities/cached/${encodeURIComponent(entityId)}`,
        );
      }
      if (isExternalEntityId) {
        return get<FullEntityResponse>(
          `/v1/unit21/entities/${encodeURIComponent(entityId)}`,
        );
      }
      return get<FullEntityResponse>(
        `/v1/entities/${encodeURIComponent(entityId)}`,
      );
    },
    queryKey: ENTITY_QUERY_KEYS.getEntityDetails(
      entityId,
      useCacheEndpoint,
      isExternalEntityId,
    ),
    staleTime: 10000,
    enabled: !!entityId && enabled && hasReadEntitiesPermission,
    refetchInterval: ({ state: { data } }) => {
      if (useCacheEndpoint) {
        if (
          data &&
          isCGDOLoadingResponse(data) &&
          dataUpdateCountRef.current < COLD_STORAGE_MAX_RETRIES
        ) {
          return getColdStorageRetryWaitTime(dataUpdateCountRef.current);
        }
      }
      // Do not retry by default
      return false;
    },
  });

  const coldStorageLoading = useMemo(
    () =>
      getLoadingFromColdStorageStateForCGDO(
        entityId,
        query.data && isCGDOLoadingResponse(query.data)
          ? query.data.data_loader
          : undefined,
      ),
    [entityId, query.data],
  );

  useEffect(() => {
    // Reset update count when the ID changes
    dataUpdateCountRef.current = 0;
  }, [entityId]);

  // Increase the countRef whenever we get a loading response
  useEffect(() => {
    if (query.data && isCGDOLoadingResponse(query.data)) {
      dataUpdateCountRef.current += 1;
    }
  }, [query.data]);

  const { data, isLoading } = useCGDOProgressiveData(
    query.data,
    query.isLoading,
    query.isError,
    select,
  );

  return {
    // This is still accesible if needed for whatever reason
    query,
    // The following values already take into account the cold storage loading state
    data,
    isLoading,
    coldStorageLoading,
  };
};
